// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { SyntheticEvent, useContext, useMemo } from 'react';
import styled from '@emotion/styled';

import {
  spacing,
  fontSize,
  fontWeight,
  mediaMax,
  isMediaMax,
} from 'presentations/views/styles';
import DateController, {
  DateControllerActionType,
} from 'presentations/views/components/molecules/DateController';
import {
  black,
  gray01,
  gray02,
  gray03,
  gray04,
  white,
} from 'presentations/views/styles/color';
import Icon from 'presentations/views/components/atoms/Icon';
import Link from 'presentations/views/components/atoms/Link';
import { ViewerItem } from 'presentations/api/schema/user';
import { CalendarContext } from '../../atoms/Calendar/reducer/CalendarContext';

import Avatar, { Group } from 'presentations/views/components/atoms/Avatar';
import CalendarViewTypeField from 'presentations/views/components/molecules/CalendarViewTypeField';
import { TeamUserListItem } from 'presentations/api/schema/team_user';

export interface Props {
  viewer: ViewerItem;
  onChangeTerm: (action?: DateControllerActionType) => void;
  selectedTeamUsers: TeamUserListItem[];
  selectableTeamUsers: TeamUserListItem[];
  isOpenUserList: boolean;
  onClickOpenUserList: () => void;
  onClickSetting: (e: SyntheticEvent<HTMLInputElement>) => void;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const { state, dispatch } = useContext(CalendarContext);

  const isSmartphone = isMediaMax('smartphone');

  const viewerTeamUser = useMemo(() => {
    return props.selectableTeamUsers.find(
      (teamUser) => teamUser.userId === props.viewer.uid,
    );
  }, []);

  const handleChangeTerm = (action: DateControllerActionType) => {
    props.onChangeTerm(action);
  };

  return (
    <Container>
      <LeftContainer>
        <DateTitleContainer>
          <DateTitle>
            {state.startedAt.toFormat(
              t(isSmartphone ? 'Date Title for mobile' : 'Date Title'),
            )}
          </DateTitle>
        </DateTitleContainer>
        <ControlContainer data-intercom-target="calendar-switch-view">
          <CalendarMoveButtonContainer>
            <DateController
              handleChangeTerm={handleChangeTerm}
              variant="filled"
              onlyToday={isSmartphone}
            />
          </CalendarMoveButtonContainer>
        </ControlContainer>
      </LeftContainer>
      <MainContainer>
        <CalendarViewTypeField />
      </MainContainer>
      <RightContainer>
        <UserListContainer onClick={props.onClickOpenUserList}>
          <GroupWrap max={3} backgroundColor={viewerTeamUser?.color}>
            {props.selectableTeamUsers.map((teamUser) => {
              const isActive = props.selectedTeamUsers.some(
                (u) => u.userId === teamUser.userId,
              );
              return (
                <UserAvatar
                  key={teamUser.userId}
                  isActive={isActive}
                  src={teamUser.profileImage}
                  color={teamUser.color}
                >
                  {teamUser.name || ''}
                </UserAvatar>
              );
            })}
          </GroupWrap>
          <UserListIcon
            name={props.isOpenUserList ? 'expand_less' : 'expand_more'}
          />
        </UserListContainer>
        <FilterLink onClick={props.onClickSetting}>
          <FilterIcon name="settings" size="20px" />
        </FilterLink>
      </RightContainer>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 56px;
  padding: 0 ${spacing(16)};
  background-color: ${white};
  border-bottom: 1px solid ${gray04};

  ${mediaMax.desktop} {
    min-width: 500px;
  }
  ${mediaMax.tablet} {
    position: sticky;
    min-width: initial;
    height: initial;
    padding: ${spacing(8)} 0 ${spacing(8)} ${spacing(16)};
    /* flex-direction: column; */
    align-items: initial;
    min-width: auto;
    /* min-height: 80px; */
  }
`;

const LeftContainer = styled.div`
  display: flex;
  align-items: center;

  ${mediaMax.tablet} {
    min-height: 40px;
  }
`;

const DateTitleContainer = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-right: ${spacing(12)};
`;
const DateTitle = styled.span`
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(20)};
  line-height: 150%;
  ${mediaMax.tablet} {
    font-size: ${fontSize(18)};
  }
`;

const ControlContainer = styled.div`
  display: flex;
  justify-content: space-between;

  ${mediaMax.tablet} {
    align-items: center;
    margin-top: ${spacing(4)};
  }
`;
const CalendarMoveButtonContainer = styled.div``;

const RightContainer = styled.div`
  display: flex;
  align-items: center;
`;

const NavigationLink = styled(Link)`
  margin-right: ${spacing(16)};
  display: flex;
  align-items: center;
  color: ${gray01};
  font-size: ${fontSize(14)};
  &:hover {
    color: ${black};
  }
  &:last-of-type {
    margin-right: 0;
  }
  ${mediaMax.smartphone} {
    margin-right: ${spacing(8)};
  }
`;
const TypeIcon = styled(Icon)`
  margin-right: ${spacing(4)};
  width: 24px !important;
  height: 24px !important;
  color: ${gray01};

  ${mediaMax.smartphone} {
    font-size: ${fontSize(20)} !important;
    width: 20px !important;
    height: 20px !important;
  }
`;
const ExpandIcon = styled(Icon)`
  min-width: 20px;
  min-height: 20px;
`;

const FilterLink = styled(Link)`
  padding: 0 ${spacing(12)};
  border-left: 1px solid ${gray03};
  display: flex;
  align-items: center;
`;
const FilterIcon = styled(Icon)`
  color: ${gray01};
`;

const UserListContainer = styled.div`
  margin-right: ${spacing(12)};
  display: flex;
  align-items: center;
  cursor: pointer;
`;
const GroupWrap = styled(Group)<{ backgroundColor?: string }>`
  .MuiAvatarGroup-avatar.MuiAvatar-colorDefault {
    width: 30px;
    height: 30px;
    padding: 2px;
    font-size: ${fontSize(14)};
    font-weight: bold;
    background-color: ${(props) => props.backgroundColor};
  }
`;
const UserAvatar = styled(Avatar)<{ isActive: boolean; color: string }>`
  width: 30px;
  height: 30px;
  opacity: ${(props) => !props.isActive && '0.5'};
  border: ${(props) => `2px solid ${props.color} !important`};
  padding: 2px;
  background-color: ${white};
  img {
    border-radius: 50%;
  }
`;
const UserListIcon = styled(Icon)`
  width: 20px !important;
  height: 20px !important;
  color: ${gray02};
`;

const MainContainer = styled.div`
  display: flex;
  align-items: center;
  ${mediaMax.tablet} {
    display: none;
  }
`;
